import { useCallback } from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";

export type FilterType = {
  id: string;
  field: string;
  label: string;
  value: string;
  operator: "contains" | "equals" | "starts_with" | "ends_with";
};

export interface FiltersFormData {
  filters: FilterType[];
  searchValue: string;
}

export function useFilters() {
  const { control, getValues, setValue, watch, reset } =
    useForm<FiltersFormData>({
      defaultValues: {
        filters: [],
        searchValue: "",
      },
    });

  const { append, remove, update } = useFieldArray({
    control,
    name: "filters",
  });

  const watchedFilters = useWatch({ control, name: "filters" });
  const watchedSearchValue = useWatch({ control, name: "searchValue" });

  const addFilter = useCallback(
    (field: string, label: string) => {
      const newFilter: FilterType = {
        id: `${field}_${Date.now()}`,
        field,
        label,
        value: "",
        operator: "contains",
      };
      append(newFilter);
    },
    [append],
  );

  const removeFilter = useCallback(
    (filterId: string) => {
      const currentFilters = getValues("filters");
      const filterIndex = currentFilters.findIndex((f) => f.id === filterId);
      if (filterIndex >= 0) {
        remove(filterIndex);
      }
    },
    [remove, getValues],
  );

  const updateFilter = useCallback(
    (filterId: string, updates: Partial<FilterType>) => {
      const currentFilters = getValues("filters");
      const filterIndex = currentFilters.findIndex((f) => f.id === filterId);
      if (filterIndex >= 0) {
        const updatedFilter = { ...currentFilters[filterIndex], ...updates };
        update(filterIndex, updatedFilter);
      }
    },
    [update, getValues],
  );

  const clearAllFilters = useCallback(() => {
    reset({
      filters: [],
      searchValue: "",
    });
  }, [reset]);

  const getActiveFiltersCount = useCallback(() => {
    return watchedFilters?.filter((f) => f.value.trim() !== "").length || 0;
  }, [watchedFilters]);

  const setSearchValue = useCallback(
    (value: string) => {
      setValue("searchValue", value);
    },
    [setValue],
  );

  return {
    filters: watchedFilters || [],
    searchValue: watchedSearchValue || "",
    addFilter,
    removeFilter,
    updateFilter,
    clearAllFilters,
    getActiveFiltersCount,
    setSearchValue,
    control,
    formMethods: {
      getValues,
      setValue,
      watch,
      reset,
    },
  };
}
